import React, { useState } from "react";
import {
  Dialog, Box, Typography, IconButton, List, ListItemButton, ListItemText, Chip, Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";

const BLUE = "#1E6FE8", NAVY = "#0E2A5A", INK = "#0f172a", MUTED = "#64748b", LINE = "#e6ebf3";

// Resources shown in the "Resources" category. Add more entries here as guides/videos are produced.
const RESOURCES = [
  {
    id: "ar-upload",
    type: "video",
    title: "How to upload an AR file",
    desc: "A guided, narrated walkthrough: what to prepare, the import steps, and where your scored results appear.",
    duration: "~3 min",
    url: `${process.env.PUBLIC_URL || ""}/help/ar-upload-walkthrough.html`,
  },
];

const CATEGORIES = [
  { key: "getting-started", label: "Getting Started", Icon: RocketLaunchOutlinedIcon },
  { key: "resources", label: "Resources", Icon: FolderOpenOutlinedIcon },
  { key: "faq", label: "FAQ", Icon: HelpOutlineIcon },
];

const FAQ = [
  { q: "What file formats can I upload for AR?", a: "Excel (.xlsx). It can span multiple sheets — aging detail, a customer summary, payments, and an on-hold/blocked list. You don't need a specific layout; AIArc reads the format you have." },
  { q: "Where do the scores appear after import?", a: "In the AR Summary table on the tenant dashboard — each customer gets a Payment Index, an ARC risk score (1–10), a recommended limit, and aging buckets. Click a customer to open its full report." },
  { q: "What does “Age by statement date” do?", a: "It ages invoices as of the statement date instead of today, so a bill that wasn't yet due isn't wrongly counted overdue." },
  { q: "Why is a customer's refresh icon spinning?", a: "That customer is being (re)scored. Scores fill in live — the spinner clears when underwriting finishes." },
];

export default function HelpCenter({ open, onClose }) {
  const [category, setCategory] = useState("resources");
  const [activeResource, setActiveResource] = useState(null);

  const close = () => { setActiveResource(null); onClose && onClose(); };

  return (
    <Dialog open={!!open} onClose={close} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden", height: "84vh", maxHeight: 760 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 2, background: NAVY, color: "#fff" }}>
        <MenuBookOutlinedIcon />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 17, lineHeight: 1.1 }}>Help Center</Typography>
          <Typography sx={{ fontSize: 12.5, color: "#cdd8ee" }}>Guides, resources, and answers to get the most out of CrediArc.</Typography>
        </Box>
        <IconButton onClick={close} sx={{ color: "#cdd8ee" }} aria-label="Close help center"><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Category rail */}
        <Box sx={{ width: 220, flexShrink: 0, borderRight: `1px solid ${LINE}`, py: 1.5, background: "#f8fafc" }}>
          <List disablePadding>
            {CATEGORIES.map(({ key, label, Icon }) => (
              <ListItemButton key={key} selected={category === key}
                onClick={() => { setCategory(key); setActiveResource(null); }}
                sx={{ mx: 1, borderRadius: "10px", mb: 0.5,
                  "&.Mui-selected": { background: "#e9f1ff" }, "&.Mui-selected:hover": { background: "#e0ebff" } }}>
                <Icon sx={{ fontSize: 19, mr: 1.25, color: category === key ? BLUE : MUTED }} />
                <ListItemText primaryTypographyProps={{ fontSize: 14, fontWeight: category === key ? 700 : 500, color: category === key ? BLUE : INK }} primary={label} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", p: 3, background: "#fff" }}>
          {/* Resource viewer (video) */}
          {activeResource ? (
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveResource(null)}
                sx={{ textTransform: "none", color: MUTED, alignSelf: "flex-start", mb: 1.5 }}>
                Back to Resources
              </Button>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: INK, mb: 1 }}>{activeResource.title}</Typography>
              <Box sx={{ flex: 1, border: `1px solid ${LINE}`, borderRadius: "12px", overflow: "hidden", minHeight: 380 }}>
                <iframe title={activeResource.title} src={activeResource.url}
                  style={{ width: "100%", height: "100%", border: 0 }} allow="autoplay" />
              </Box>
            </Box>
          ) : category === "resources" ? (
            <>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: INK, mb: 0.5 }}>Resources</Typography>
              <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>Watch a walkthrough or open a step-by-step guide.</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                {RESOURCES.map((r) => (
                  <Box key={r.id} onClick={() => setActiveResource(r)}
                    sx={{ cursor: "pointer", border: `1px solid ${LINE}`, borderRadius: "12px", overflow: "hidden",
                      transition: "all .15s ease", "&:hover": { borderColor: BLUE, boxShadow: "0 6px 18px rgba(30,111,232,.14)", transform: "translateY(-1px)" } }}>
                    <Box sx={{ height: 116, background: "linear-gradient(135deg,#0E2A5A,#1E6FE8)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                      <PlayCircleOutlineIcon sx={{ color: "#fff", fontSize: 46, opacity: .95 }} />
                      <Chip size="small" label={r.type === "video" ? "Video" : "Guide"}
                        sx={{ position: "absolute", top: 10, left: 10, height: 20, fontSize: 11, fontWeight: 700, color: NAVY, background: "#FFD34E" }} />
                      {r.duration && <Chip size="small" label={r.duration}
                        sx={{ position: "absolute", bottom: 10, right: 10, height: 20, fontSize: 11, fontWeight: 700, color: "#fff", background: "rgba(0,0,0,.35)" }} />}
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: INK }}>{r.title}</Typography>
                      <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.5, lineHeight: 1.45 }}>{r.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          ) : category === "getting-started" ? (
            <>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: INK, mb: 0.5 }}>Getting Started</Typography>
              <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>The fastest path to a scored receivables book.</Typography>
              <Box component="ol" sx={{ pl: 2.5, m: 0, "& li": { fontSize: 13.5, color: INK, mb: 1, lineHeight: 1.5 } }}>
                <li>Open <b>Upload Company Files</b> and drop your AR Excel export.</li>
                <li>Set the import options (age by statement date, net settlements, replace existing).</li>
                <li>Review what AIArc understood — correct it in the chat if needed.</li>
                <li>Click <b>Looks right — import</b>. Customers appear and are scored live.</li>
                <li>Read the results in the <b>AR Summary</b> table and each company's report.</li>
              </Box>
              <Button variant="contained" onClick={() => { setCategory("resources"); setActiveResource(RESOURCES[0]); }}
                startIcon={<PlayCircleOutlineIcon />} sx={{ mt: 2.5, textTransform: "none", fontWeight: 700, background: BLUE }}>
                Watch the AR upload walkthrough
              </Button>
            </>
          ) : (
            <>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: INK, mb: 1.5 }}>Frequently asked questions</Typography>
              {FAQ.map((f, idx) => (
                <Box key={idx} sx={{ borderBottom: `1px solid ${LINE}`, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: INK, mb: 0.5 }}>{f.q}</Typography>
                  <Typography sx={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>{f.a}</Typography>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
